/**
 * Version-update service — watches the `global/version` doc that the
 * deploy pipeline writes after every deploy and flips `updateAvailable`
 * when the deployed build id differs from the one baked into this bundle.
 *
 * The banner it drives never reloads on its own: a forced reload could
 * interrupt an active VNC recording or a run being watched, so the user
 * always clicks Refresh themselves (or dismisses).
 *
 * The doc is rules-gated to `isSignedIn()`, so the subscription is bound
 * to the auth state (same pattern as OrgService) — watching while signed
 * out would error with permission-denied.
 */

import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';

import { AuthService } from '../firebase/auth.service';
import { FirestoreService } from '../firebase/firestore.service';
import { BUILD_ID } from './build-id';

interface VersionDoc {
  readonly buildId?: string;
  readonly version?: string;
}

export const VERSION_DOC_PATH = 'global/version';

@Injectable({ providedIn: 'root' })
export class VersionUpdateService {
  private readonly auth = inject(AuthService);
  private readonly firestore = inject(FirestoreService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _deployedBuildId = signal<string | undefined>(undefined);
  /** Build id the user dismissed — a NEWER deploy re-fires the banner. */
  private readonly _dismissedBuildId = signal<string | null>(null);

  readonly updateAvailable = computed(() => {
    const deployed = this._deployedBuildId();
    return !!deployed && deployed !== BUILD_ID && deployed !== this._dismissedBuildId();
  });

  private sub: Subscription | null = null;

  constructor() {
    // Bind/unbind the version-doc subscription to the signed-in state.
    effect(() => {
      const signedIn = this.auth.isSignedIn();
      this.dispose();
      this._deployedBuildId.set(undefined);
      if (!signedIn) return;
      this.sub = this.firestore.watchDoc<VersionDoc>(VERSION_DOC_PATH).subscribe({
        next: (doc) => this._deployedBuildId.set(doc?.buildId || undefined),
        // Fail silent: an unreadable doc just means no prompt.
        error: () => this._deployedBuildId.set(undefined),
      });
    });
    this.destroyRef.onDestroy(() => this.dispose());
  }

  dismiss(): void {
    this._dismissedBuildId.set(this._deployedBuildId() ?? null);
  }

  /** Delegated so component specs can spy instead of reloading jsdom. */
  reloadPage(): void {
    window.location.reload();
  }

  private dispose(): void {
    this.sub?.unsubscribe();
    this.sub = null;
  }
}
